/**
 * PortalContainerContext - Scoped Portal Target for Modals & Overlays
 *
 * Solves the "modal escapes bundle overlay" problem by providing a
 * configurable portal target for all overlay components (Modal, Tooltip, etc.).
 *
 * Without this, components using `createPortal(el, document.body)` render
 * outside the bundle overlay DOM tree, covering the BottomNavigationBar
 * and other root-level chrome.
 *
 * How it works:
 * 1. RootWrapper renders an `.app-viewport` div that wraps app content
 *    and a `.app-portal-root` div for portals, then provides the portal
 *    root via this context.
 * 2. `.app-viewport` uses CSS containment (`contain: layout style paint`)
 *    which makes `position: fixed` descendants relative to the viewport div
 *    instead of the browser viewport — like an iframe.
 * 3. Modal (and future overlay components) call `usePortalContainer()` to
 *    get the scoped target instead of hard-coding `document.body`.
 *
 * In bundle mode this keeps modals inside `.bundle-overlay__content`,
 * so `.bundle-overlay__nav` (BottomNavigationBar) stays visible.
 *
 * @example
 * ```tsx
 * // Inside a component that needs to portal
 * const portalTarget = usePortalContainer();
 * return createPortal(<Overlay />, portalTarget);
 * ```
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import './PortalContainerContext.css';

const PortalContainerContext = createContext<HTMLElement | null>(null);

/**
 * Returns the nearest portal container element.
 * Falls back to `document.body` when no provider is present
 * (e.g. in tests or outside RootLayout).
 */
export function usePortalContainer(): HTMLElement {
  const container = useContext(PortalContainerContext);
  return container ?? document.body;
}

export interface PortalContainerProviderProps {
  children: ReactNode;
}

/**
 * Provides a scoped portal container for descendant overlay components.
 *
 * Renders:
 * ```
 * <div class="app-viewport">     ← CSS containment boundary
 *   {children}                    ← app content
 *   <div class="app-portal-root"> ← portal target for modals
 * </div>
 * ```
 *
 * The `.app-viewport` div has `contain: layout style paint` which
 * creates a new containing block for `position: fixed` descendants,
 * effectively sandboxing overlays like an iframe.
 */
export function PortalContainerProvider({ children }: PortalContainerProviderProps) {
  const [portalRoot, setPortalRoot] = useState<HTMLDivElement | null>(null);

  const callbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node) setPortalRoot(node);
  }, []);

  return (
    <PortalContainerContext.Provider value={portalRoot}>
      <div className="app-viewport">
        {children}
        <div ref={callbackRef} className="app-portal-root" />
      </div>
    </PortalContainerContext.Provider>
  );
}
